import pako from "pako";

/**
 * Reads the header row (row 1) of an uploaded spreadsheet file, fully
 * client-side, with no external spreadsheet library.
 *
 * - CSV: parsed as text.
 * - XLSX: an .xlsx file is a ZIP archive of XML parts. We parse the ZIP
 *   central directory by hand, inflate the needed parts with `pako`
 *   (already a project dependency) and parse the XML with the native
 *   `DOMParser`.
 *
 * @param {File} file - the user-selected file (.xlsx or .csv).
 * @returns {Promise<string[]>} header cell values, indexed by column.
 */
export default function readSpreadsheetHeader(file) {
  if (!file) {
    return Promise.reject(new Error("No file provided"));
  }

  const name = (file.name || "").toLowerCase();

  if (name.endsWith(".csv")) {
    return readFileAsText(file).then(parseCsvHeader);
  }

  if (name.endsWith(".xlsx")) {
    return readFileAsArrayBuffer(file).then(parseXlsxHeader);
  }

  return Promise.reject(new Error("Unsupported file type. Please upload an .xlsx or .csv file."));
}

/**
 * Reads every row of an uploaded spreadsheet file, returning the header row
 * and the data rows separately. Used by importers that need to transform
 * rows client-side before re-uploading.
 *
 * @param {File} file
 * @returns {Promise<{ headers: string[], rows: string[][] }>}
 */
export function readSpreadsheetAllRows(file) {
  if (!file) {
    return Promise.reject(new Error("No file provided"));
  }

  const name = (file.name || "").toLowerCase();

  if (name.endsWith(".csv")) {
    return readFileAsText(file).then(parseCsvAll);
  }

  if (name.endsWith(".xlsx")) {
    return readFileAsArrayBuffer(file).then(parseXlsxAll);
  }

  return Promise.reject(new Error("Unsupported file type. Please upload an .xlsx or .csv file."));
}

/**
 * Build a UTF-8 CSV string from a 2D array. Used after client-side transforms.
 */
export function buildCsv(rows) {
  return rows.map((row) => row.map(escapeCsvCell).join(",")).join("\r\n");
}

function escapeCsvCell(value) {
  const s = String(value == null ? "" : value);
  if (/[",\r\n]/.test(s)) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

/* ------------------------------------------------------------------ */
/* File readers                                                        */
/* ------------------------------------------------------------------ */

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read the file"));
    reader.readAsText(file);
  });
}

function readFileAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Could not read the file"));
    reader.readAsArrayBuffer(file);
  });
}

/* ------------------------------------------------------------------ */
/* CSV                                                                 */
/* ------------------------------------------------------------------ */

function parseCsvHeader(text) {
  // Strip a leading UTF-8 BOM, then take the first non-empty line.
  const clean = text.replace(/^\uFEFF/, "");
  const firstLine = clean.split(/\r\n|\r|\n/).find((l) => l.trim() !== "");

  if (!firstLine) {
    throw new Error("The file appears to be empty");
  }

  const delimiter = detectDelimiter(firstLine);
  return parseDelimitedLine(firstLine, delimiter);
}

function detectDelimiter(line) {
  const candidates = [",", ";", "\t"];
  let best = ",";
  let bestCount = -1;

  candidates.forEach((d) => {
    const count = parseDelimitedLine(line, d).length;
    if (count > bestCount) {
      bestCount = count;
      best = d;
    }
  });

  return best;
}

function parseCsvAll(text) {
  const clean = text.replace(/^\uFEFF/, "");
  const firstLine = clean.split(/\r\n|\r|\n/).find((l) => l.trim() !== "");

  if (!firstLine) {
    throw new Error("The file appears to be empty");
  }

  const delimiter = detectDelimiter(firstLine);
  const allRows = parseCsvText(clean, delimiter).filter((row) => row.some((cell) => cell !== ""));

  if (!allRows.length) {
    throw new Error("The file appears to be empty");
  }

  return {
    headers: allRows[0],
    rows: allRows.slice(1),
  };
}

// Full CSV parser — handles quoted fields, escaped quotes, multi-line cells.
function parseCsvText(text, delimiter) {
  const rows = [];
  let row = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      row.push(current);
      current = "";
    } else if (ch === "\r") {
      // swallow — '\n' will close the row
    } else if (ch === "\n") {
      row.push(current);
      rows.push(row);
      row = [];
      current = "";
    } else {
      current += ch;
    }
  }

  // Flush the final row if the file didn't end with a newline.
  if (current !== "" || row.length > 0) {
    row.push(current);
    rows.push(row);
  }

  return rows.map((r) => r.map((c) => c.trim()));
}

function parseDelimitedLine(line, delimiter) {
  const result = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      result.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  result.push(current);

  return result.map((cell) => cell.trim());
}

/* ------------------------------------------------------------------ */
/* XLSX (ZIP + XML)                                                     */
/* ------------------------------------------------------------------ */

const ZIP_EOCD_SIG = 0x06054b50;
const ZIP_CD_SIG = 0x02014b50;
const ZIP_LFH_SIG = 0x04034b50;

function parseXlsxHeader(buffer) {
  const entries = parseZipCentralDirectory(buffer);

  const sharedStringsXml = entries["xl/sharedStrings.xml"]
    ? extractZipEntry(buffer, entries["xl/sharedStrings.xml"])
    : "";
  const sharedStrings = parseSharedStrings(sharedStringsXml);

  const sheetPath = resolveFirstSheetPath(buffer, entries);
  if (!sheetPath || !entries[sheetPath]) {
    throw new Error("Could not find a worksheet in the file");
  }

  const sheetXml = extractZipEntry(buffer, entries[sheetPath]);
  return parseWorksheetHeaderRow(sheetXml, sharedStrings);
}

function parseXlsxAll(buffer) {
  const entries = parseZipCentralDirectory(buffer);

  const sharedStringsXml = entries["xl/sharedStrings.xml"]
    ? extractZipEntry(buffer, entries["xl/sharedStrings.xml"])
    : "";
  const sharedStrings = parseSharedStrings(sharedStringsXml);

  const sheetPath = resolveFirstSheetPath(buffer, entries);
  if (!sheetPath || !entries[sheetPath]) {
    throw new Error("Could not find a worksheet in the file");
  }

  const sheetXml = extractZipEntry(buffer, entries[sheetPath]);
  return parseWorksheetAllRows(sheetXml, sharedStrings);
}

function parseWorksheetAllRows(xml, sharedStrings) {
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  const rowEls = doc.getElementsByTagName("row");

  if (!rowEls.length) {
    throw new Error("The worksheet has no rows");
  }

  const allRows = [];
  let maxColumns = 0;

  for (let r = 0; r < rowEls.length; r++) {
    const cells = rowEls[r].getElementsByTagName("c");
    const row = [];
    for (let i = 0; i < cells.length; i++) {
      const cell = cells[i];
      const ref = cell.getAttribute("r") || "";
      const columnIndex = ref ? columnRefToIndex(ref) : i;
      row[columnIndex] = readCellText(cell, sharedStrings);
    }
    for (let i = 0; i < row.length; i++) {
      if (row[i] == null) row[i] = "";
    }
    if (row.length > maxColumns) maxColumns = row.length;
    allRows.push(row);
  }

  // Pad every row so all rows share the same column count.
  for (let r = 0; r < allRows.length; r++) {
    while (allRows[r].length < maxColumns) allRows[r].push("");
  }

  const nonEmpty = allRows.filter((row) => row.some((cell) => cell !== ""));
  if (!nonEmpty.length) {
    throw new Error("The worksheet has no rows");
  }

  return {
    headers: nonEmpty[0],
    rows: nonEmpty.slice(1),
  };
}

function parseZipCentralDirectory(buffer) {
  const view = new DataView(buffer);
  const length = view.byteLength;

  // The End Of Central Directory record sits within the last 22 + 65535 bytes.
  const eocdOffset = findEocdOffset(view);
  if (eocdOffset < 0) {
    throw new Error("This does not look like a valid .xlsx file");
  }

  const recordCount = view.getUint16(eocdOffset + 10, true);
  const cdOffset = view.getUint32(eocdOffset + 16, true);
  const decoder = new TextDecoder();

  const entries = {};
  let pointer = cdOffset;

  for (let i = 0; i < recordCount && pointer + 46 <= length; i++) {
    if (view.getUint32(pointer, true) !== ZIP_CD_SIG) {
      break;
    }

    const method = view.getUint16(pointer + 10, true);
    const compressedSize = view.getUint32(pointer + 20, true);
    const nameLength = view.getUint16(pointer + 28, true);
    const extraLength = view.getUint16(pointer + 30, true);
    const commentLength = view.getUint16(pointer + 32, true);
    const localOffset = view.getUint32(pointer + 42, true);
    const name = decoder.decode(new Uint8Array(buffer, pointer + 46, nameLength));

    entries[name] = { method, compressedSize, localOffset };
    pointer += 46 + nameLength + extraLength + commentLength;
  }

  return entries;
}

function findEocdOffset(view) {
  const length = view.byteLength;
  const minOffset = Math.max(0, length - (22 + 0xffff));

  for (let i = length - 22; i >= minOffset; i--) {
    if (view.getUint32(i, true) === ZIP_EOCD_SIG) {
      return i;
    }
  }
  return -1;
}

function extractZipEntry(buffer, entry) {
  const view = new DataView(buffer);

  if (view.getUint32(entry.localOffset, true) !== ZIP_LFH_SIG) {
    throw new Error("The .xlsx file is corrupt");
  }

  // The local header's extra-field length can differ from the central
  // directory, so the data offset must be recomputed here.
  const nameLength = view.getUint16(entry.localOffset + 26, true);
  const extraLength = view.getUint16(entry.localOffset + 28, true);
  const dataStart = entry.localOffset + 30 + nameLength + extraLength;
  const compressed = new Uint8Array(buffer, dataStart, entry.compressedSize);

  let bytes;
  if (entry.method === 0) {
    bytes = compressed; // stored, uncompressed
  } else if (entry.method === 8) {
    bytes = pako.inflateRaw(compressed); // deflate
  } else {
    throw new Error("Unsupported compression inside the .xlsx file");
  }

  return new TextDecoder().decode(bytes);
}

function resolveFirstSheetPath(buffer, entries) {
  // Preferred: follow workbook.xml -> workbook.xml.rels to the first sheet.
  if (entries["xl/workbook.xml"] && entries["xl/_rels/workbook.xml.rels"]) {
    try {
      const workbookXml = extractZipEntry(buffer, entries["xl/workbook.xml"]);
      const relsXml = extractZipEntry(buffer, entries["xl/_rels/workbook.xml.rels"]);

      const workbookDoc = new DOMParser().parseFromString(workbookXml, "application/xml");
      const sheetEl = workbookDoc.getElementsByTagName("sheet")[0];
      const relId =
        sheetEl &&
        (sheetEl.getAttribute("r:id") ||
          sheetEl.getAttributeNS(
            "http://schemas.openxmlformats.org/officeDocument/2006/relationships",
            "id"
          ));

      if (relId) {
        const relsDoc = new DOMParser().parseFromString(relsXml, "application/xml");
        const relationships = relsDoc.getElementsByTagName("Relationship");
        for (let i = 0; i < relationships.length; i++) {
          if (relationships[i].getAttribute("Id") === relId) {
            let target = relationships[i].getAttribute("Target") || "";
            target = target.replace(/^\/?xl\//, "").replace(/^\//, "");
            return "xl/" + target;
          }
        }
      }
    } catch (e) {
      // fall through to the heuristic below
    }
  }

  // Fallback: lowest-numbered worksheet file.
  const sheetFiles = Object.keys(entries)
    .filter((key) => /^xl\/worksheets\/sheet\d+\.xml$/.test(key))
    .sort((a, b) => sheetNumber(a) - sheetNumber(b));

  return sheetFiles[0] || null;
}

function sheetNumber(path) {
  const match = path.match(/sheet(\d+)\.xml$/);
  return match ? Number(match[1]) : 0;
}

function parseSharedStrings(xml) {
  if (!xml) {
    return [];
  }

  const doc = new DOMParser().parseFromString(xml, "application/xml");
  const items = doc.getElementsByTagName("si");
  const strings = [];

  for (let i = 0; i < items.length; i++) {
    // <si> may hold a single <t> or rich-text runs of <r><t>.
    const textNodes = items[i].getElementsByTagName("t");
    let value = "";
    for (let j = 0; j < textNodes.length; j++) {
      value += textNodes[j].textContent || "";
    }
    strings.push(value);
  }

  return strings;
}

function parseWorksheetHeaderRow(xml, sharedStrings) {
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  const rows = doc.getElementsByTagName("row");

  if (!rows.length) {
    throw new Error("The worksheet has no rows");
  }

  const cells = rows[0].getElementsByTagName("c");
  const headers = [];

  for (let i = 0; i < cells.length; i++) {
    const cell = cells[i];
    const ref = cell.getAttribute("r") || "";
    const columnIndex = ref ? columnRefToIndex(ref) : i;
    headers[columnIndex] = readCellText(cell, sharedStrings);
  }

  // Fill gaps left by skipped/empty header cells.
  for (let i = 0; i < headers.length; i++) {
    if (headers[i] == null) {
      headers[i] = "";
    }
  }

  return headers;
}

function readCellText(cell, sharedStrings) {
  const type = cell.getAttribute("t");

  if (type === "s") {
    const valueEl = cell.getElementsByTagName("v")[0];
    if (!valueEl) {
      return "";
    }
    const index = Number(valueEl.textContent);
    return String(sharedStrings[index] || "").trim();
  }

  if (type === "inlineStr") {
    const textNodes = cell.getElementsByTagName("t");
    let value = "";
    for (let i = 0; i < textNodes.length; i++) {
      value += textNodes[i].textContent || "";
    }
    return value.trim();
  }

  const valueEl = cell.getElementsByTagName("v")[0];
  return valueEl ? String(valueEl.textContent || "").trim() : "";
}

function columnRefToIndex(ref) {
  const letters = ref.replace(/[^A-Za-z]/g, "").toUpperCase();
  let index = 0;
  for (let i = 0; i < letters.length; i++) {
    index = index * 26 + (letters.charCodeAt(i) - 64);
  }
  return index - 1;
}
